import { type UpdateIntegrationSettings } from '@vy/types';
import { getModifiedFields } from '@vy/utils';
import { useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { useUpdateIntegrationConfiguration } from '../../../../../../../hooks/requests/use-update-integration-configuration';
import { queryKeys } from '../../../../../../../services/query-key/query-keys';
import { runMutation } from '../../../../../../../utils/vy-mutation';
import type { HandleUpdateConfigurationSuccess } from '../../../../../IntegrationProviderPage/integration-provider-page-components/ConfigurationTabContent/configuration-tab-content-types';

export function useModifyConfiguration(handleMutationSuccess?: HandleUpdateConfigurationSuccess) {
  const queryClient = useQueryClient();
  const updateConfigurationMutation = useUpdateIntegrationConfiguration();

  return useCallback(
    async (
      integrationId: string,
      updatedIntegrationSettings: UpdateIntegrationSettings,
      existingIntegrationSettings: UpdateIntegrationSettings,
    ) => {
      const modifiedIntegrationSettings: UpdateIntegrationSettings = {
        ...getModifiedFields(updatedIntegrationSettings, existingIntegrationSettings),
        integrationProvider: existingIntegrationSettings.integrationProvider,
      };

      await runMutation(
        updateConfigurationMutation,
        { integrationId, integrationSettings: modifiedIntegrationSettings },
        {
          pending: `Updating ${updatedIntegrationSettings.integrationProvider} configuration...`,
          success: `${updatedIntegrationSettings.integrationProvider} configuration updated`,
          error: `Failed to update ${updatedIntegrationSettings.integrationProvider} configuration`,
        },
        {
          onSuccess: async (data) => {
            handleMutationSuccess?.(data);
            await queryClient.invalidateQueries(queryKeys.integrationSettings());
          },
        },
      );
    },
    [handleMutationSuccess, queryClient, updateConfigurationMutation],
  );
}
